import { DialogTopicArray } from "./dialog"
import { FilledSlot, MessageSlots } from "../slot"
import { UploadedFile } from "../../utility/common/files"
import { DialogSendButtonsKeys } from "../projectSettings"

export type MessageSender = {
    Id: string
}

export type Message = {
    MsgId: string
    MsgType: MessageType
    Direction: MessageDirection
    IdInChannel: string
    ChannelId?: string
    QuotedMessage?: QuotedMessage
    ReplyToMessage?: Message
    Attachment?: MessageAttachment
    Attachments?: MessageAttachment[]
    Sticker?: Sticker
    Text: string
    Actions: Action[]
    ClientId: number
    OperatorId?: number
    OperatorName: string
    CallCenterOperatorId?: string
    DialogID: string
    UserPic?: string
    UserName?: string
    Context: string[][]
    IntentQaId?: string
    Flags: MessageFlags[]
    IsServiceMessage: boolean
    IsClientServiceMessage: boolean
    Added: string
    Meta?: Record<string, string>
    TextType: MessageTextType
    ParentMessageId?: string
    EditType?: EditEnum
}

export interface MessageOperator {
    Id: string
    Name: string
}

export enum MessageTextType {
    Text = "Text",
    Markdown = "Markdown"
}

export enum EditEnum {
    Deleted = "Deleted",
    Edited = "Edited"
}

interface ContentMessage {
    Id: string
    Direction: MessageDirection
    AddedDate: number
    Sender: MessageSender
    Text?: string
    TextType: MessageTextType
    Sticker?: Sticker
    Actions: Action[]
    Attachments: MessageAttachment[]
}

export interface UpdatedMessage extends ContentMessage {
    MessageType: MessageType
    ChannelUserId?: string
    ChannelId: string
    DialogId: string
    QuotedMessage?: QuotedMessage
    Context: string[][]
    Meta: Record<string, string>
    Status: MessageStatus
    EditType?: EditEnum
    OmniUserId?: string
}

export interface ActiveQuotedMessage extends ContentMessage {
    IsDeleted: false
}

export type DeletedQuotedMessage = Pick<UpdatedMessage, "Id" | "Direction" | "AddedDate"> & { IsDeleted: true }

export type QuotedMessage = ActiveQuotedMessage | DeletedQuotedMessage

export type PinnedMessages = {
    Messages: UpdatedMessage[]
}

export type HubPinnedMessages = {
    ActiveDialogId: string
    Messages?: UpdatedMessage[]
}

export type BadgeHoldStatusUpdate = {
    DialogId: string
    HoldStatus: string
}

export type ScreensharingMessage = {
    Id: string
    DialogId: string
    MessageType: MessageType
    Direction: MessageDirection
    Text: string
    AddedDate?: number
    Timestamp?: number
    Sender: MessageSender
}

export enum MessageType {
    Initial = 0,
    Message = 1,
    SentConfirmation = 2,
    ReferenceCreated = 3,
    ReadConfirmation = 4,
    FailedConfirmation = 5,
    UpdateDialogProgram = 6,
    WritingStop = 7,
    Deleted = 8,
    PhoneNumber = 9,
    Location = 10,
    ReceivedByMediator = 11,
    ReceivedByOperator = 12,
    OperatorIsTyping = 13,
    OperatorStoppedTyping = 14,
    UpdateDialogScore = 15,
    FinishDialog = 16,
    CloseDialogIntention = 17,
    ConnectedOperator = 18,
    DialogFinished = 19,
    AutoGreeting = 20,
    UpdateDialogNegativeReason = 21,
    UpdateUserData = 22,
    ClientHold = 23,
    UpdateDialogUsefulness = 24,
    MessageEdited = 31,
    MessageDeleted = 32,
    DialogStart = 33,
    DeletedByClient = 34,
    CobrowsingRequest = 100,
    CobrowsingStarted = 101,
    CobrowsingDenied = 102,
    CobrowsingStateUpdate = 103,
    CobrowsingFinished = 104,
    ScreensharingCobrowsingRequest = 111,
    ScreensharingVideocallRequest = 112,
    ScreensharingRequestTimeout = 113,
    ScreensharingRequestDecline = 114,
    ScreensharingConnectionStarted = 115,
    ScreensharingConnectionFailed = 116,
    ScreensharingSessionStarted = 117,
    ScreensharingSessionFinished = 118,
    ScreensharingFinishReason = 119,
    ScreensharingAccessPermissionMissing = 120,
    InternalOperatorMessage = 201,
    OperatorRerouteDialog = 204,
    OperatorRerouteDialogConfirmation = 205
}

export enum MessageDirection {
    Incoming = 0,
    Outgoing = 1
}

export type MessageAttachment = UploadedFile

export enum ContentType {
    FILE = "FILE",
    VOICE = "VOICE",
    IMAGE = "IMAGE"
}

export interface Sticker {
    sticker_id: string
    sticker_url?: string
    animation_type: StickerAnimationType
}

export enum StickerAnimationType {
    NoAnimation = "NoAnimation",
    Lottie = "Lottie"
}

enum MessageFlags {
    Unknown = -1,
    Sent = 0,
    Delivered = 1,
    Read = 2,
    Failed = 3,
    Deleted = 4,
    Phone = 5,
    Location = 6,
    AutoGreeting = 7
}

export interface Action {
    action_id: string
    action_text: string
}

// здесь стоит UpdatedMessage
// но на беке есть некий ApiMessage, который отличается
// при создании реплаев это нужно учесть
// https://gitlab.crafttalk.ru/aibotdev/core/opbot/-/blob/develop/src/opbot/Workplace/V2/Dto/Messages.fs#L42
export interface SendMessageRequest {
    OmniUserId: string
    ChannelId: string
    StartNewDialog: boolean
    QuotedMessageId?: string
    Attachments: MessageAttachment[]
    Sticker?: Sticker
    Text: string
    Actions: Action[]
    Meta: Record<string, string>
    Slots: MessageSlots
    ChannelUserId?: string
}

export interface SendIntentRequest {
    ProjectId: string
    OmniUserId?: string
    ChannelUserId?: string
    ChannelId: string
    StartNewDialog: boolean
    IntentId: string
    DoDetachOperator: boolean
    UpdatedSlots?: FilledSlot[]
    Meta?: MessageMeta
}

export interface ISendFinishRequest {
    OmniUserId: string
    ChannelId: string
    ProjectId: string
    Topics?: DialogTopicArray[]
}

export interface MergedMessageRequest {
    projectId: string
    message: SendMessageRequest
    body: GetUpdatedMessagesRequest
    quotedMessage?: QuotedMessage
}

export interface InternalMessageRequestDTO {
    projectId: string
    message: SendInternalMessageRequest
    body: GetUpdatedMessagesRequest
}

export interface SendInternalMessageRequest {
    OmniUserId: string
    ChannelId: string
    QuotedMessageId?: string
    Attachments: MessageAttachment[]
    Sticker?: Sticker
    Slots: MessageSlots
    Text: string
    Meta: Record<string, string>
    Actions?: Action[]
}

export enum SendInternalStatuses {
    SentToExistingDialog = 1,
    DialogDoesNotExist = 2,
    QuotedMessageDoesNotExist = 3
}

export interface SendInternalMessageResponse {
    Status: SendInternalStatuses
    MessageId: string
}

export interface ScoreMessage {
    Id: string
    Score: string
}

export interface IdMessage {
    Id: string
}

export type DialogFinishScore = "1" | "2" | "3" | "4" | "5"

export interface DialogFinishMessage {
    Id: string
    DialogId: string
    Timestamp: number
    Score?: DialogFinishScore
}

export interface GetMessagesResponse {
    [dialogId: string]: Message[]
}

export interface GetUpdatedMessagesRequest {
    OmniUserId: string
    Count: number
    StartTime: number
    FromTodayDialogs: boolean
    ProjectId: string
}

export interface ApiMessage {
    Case: MessageCase.Message
    Fields: UpdatedMessage
}

export const isDeletedMessage = (message: ApiMessagesDU): message is ApiDeletedMessage => {
    return message.Case === "DeletedByOperator" && typeof message.Fields === "object"
}

export const isEditedMessage = (message: ApiMessagesDU): message is ApiEditMessage => {
    return (
        message.Case === "EditedByOperator" && typeof message.Fields === "object" && "EditedMessageId" in message.Fields
    )
}

export enum MessageCase {
    Message = "Message",
    Score = "Score",
    DialogFinish = "DialogFinish",
    DeletedByOperator = "DeletedByOperator",
    DeletedByClient = "DeletedByClient",
    EditedByOperator = "EditedByOperator",
    DialogStart = "DialogStart",
    OperatorRerouteDialogConfirmation = "OperatorRerouteDialogConfirmation",
    OperatorRerouteDialog = "OperatorRerouteDialog",
    Confirmation = "Confirmation",
    UserTyping = "UserTyping",
    ScreensharingMessage = "ScreensharingMessage"
}

export enum UserTypingType {
    Unknown = 0,
    Typing = 1,
    Stopped = 2
}

type UserTypingMessage = {
    Id: string
    Type: UserTypingType
    Timestamp: number
    DialogId: string
    Text: string
}

export interface ApiUserTypingMessage {
    Case: MessageCase.UserTyping
    Fields: UserTypingMessage
}

export interface ApiMessage {
    Case: MessageCase.Message
    Fields: UpdatedMessage
}

export interface ApiConfirmation {
    Case: MessageCase.Confirmation
    Fields: ConfirmationMessage
}

export interface ConfirmationMessage {
    Id: string
    RelatedMessage: string
    Type: SignalRConfirmationType
    DialogId: string
}

export interface ApiScore {
    Case: MessageCase.Score
    Fields: ScoreMessage
}

export interface ApiDialogFinish {
    Case: MessageCase.DialogFinish
    Fields: DialogFinishMessage
}

export type ApiDeletedMessage = {
    Case: MessageCase.DeletedByOperator
    Fields: DeletedMessage
}

export type ApiDeletedMessageByClient = {
    Case: MessageCase.DeletedByClient
    Fields: UpdatedMessage
}

export interface ApiDialogStart {
    Case: MessageCase.DialogStart
    Fields: DialogStartMessage
}

export interface ApiDialogOperatorRerouteConfirmation {
    Case: MessageCase.OperatorRerouteDialog | MessageCase.OperatorRerouteDialogConfirmation
    Fields: DialogOperatorRerouteConfirmation
}

export interface EditedMessage extends UpdatedMessage {
    EditedMessageId: string
}

export type ApiEditMessage = {
    Case: MessageCase.EditedByOperator
    Fields: EditedMessage
}

export type ApiScreensharingMessage = {
    Case: MessageCase.ScreensharingMessage
    Fields: ScreensharingMessage
}

export type DeletedMessage = {
    DeletedMessageId: string
    DialogId: string
    Id: string
    Timestamp: number
}

export type DialogStartMessage = {
    Id: string
    DialogId: string
    Timestamp: number
    ChannelId: string
    Direction: MessageDirection
}

export type DialogOperatorRerouteConfirmation = {
    Id: string
    DialogId: string
    Timestamp: number
    IsSupervisor: boolean
    From: {
        OperatorId?: string
        QueueName: string
    }
    To: {
        OperatorId?: string
        QueueName: string
    }
}

export type ApiMessagesDU =
    | ApiMessage
    | ApiScore
    | ApiDialogFinish
    | ApiDeletedMessage
    | ApiEditMessage
    | ApiDialogStart
    | ApiConfirmation
    | ApiDialogOperatorRerouteConfirmation
    | ApiScreensharingMessage
    | ApiUserTypingMessage
    | ApiDeletedMessageByClient

export type EditedElement = {
    text: string
    attachments: MessageAttachment[]
    sticker?: Sticker
    id?: string
}

export type TMessageWithEditHistory = ApiMessagesDU & { EditedHistory?: EditedElement[] }

export interface GetUpdatedMessagesResponse {
    Messages: TMessageWithEditHistory[]
    HasMore: boolean
}

export enum SenderRole {
    Operator,
    Client
}

export enum SendMessageStatuses {
    SentToExistingDialog = 1,
    NewDialogWillBeStarted = 2,
    HasActiveDialogAlready = 3,
    DialogDoesNotExist = 4,
    QuotedMessageDoesNotExist = 5,
    HasActiveDialogWithOperator = 6,
    HasActiveDialogWithBot = 7
}

export type SendMessageResponse = {
    Status: SendMessageStatuses
    MessageId: string
}

export interface GetMessageSenderDataRequest {
    SenderRole: SenderRole
    // A client's omniUserId or operator's userId
    Id: string
}

export enum MessageStatus {
    Unknown = 0,
    Sent = 1,
    ReceivedByMediator = 2,
    Delivered = 3,
    Read = 4,
    Failed = 5 // фейковый статус для проставления статуса Failed при получении соответствующего статуса по хабам
}

export enum SignalRConfirmationType {
    Unknown = 0,
    Sent = 1,
    Read = 2,
    Failed = 3,
    Deleted = 4,
    Edited = 5
}

export interface StartOutgoingDialogMessageRequest {
    CustomerId: string
    Text: string
    ChannelUserId: string
    ChannelId: string
    FilledSlots: MessageSlots
    Attachments: MessageAttachment[]
    Meta: MessageMeta
}

const isMessage =
    <T extends ApiMessagesDU = ApiMessage>(type: string) =>
    (check: unknown): check is T => {
        return (check as T).Fields !== undefined && (check as T).Case === type
    }

export const isApiMessage = isMessage(MessageCase.Message)
export const isConfirmationMessage = isMessage<ApiConfirmation>(MessageCase.Confirmation)
export const isUserTypingMessage = isMessage<ApiUserTypingMessage>(MessageCase.UserTyping)
export const isDeletedByClientMessage = isMessage(MessageCase.DeletedByClient)

export interface EmailValues {
    to: string
    from: string
    subject: string
    cc: string[]
    bcc: string[]
    isForwarding: boolean
}

export type MessageMeta = Record<string, string>

export interface MessageInputValue {
    text: string
    email: EmailValues | undefined
    [DialogSendButtonsKeys.Hold]: boolean
    [DialogSendButtonsKeys.InternalMessage]: boolean
    [DialogSendButtonsKeys.Message]: boolean
}

export enum AutoHoldingType {
    OperatorTimeout = "OperatorTimeout",
    ClientTimeout = "ClientTimeout"
}

export type AutoHoldingMessage = {
    DialogId: string
    AutoHolding?: AutoHoldingTimer
}

export type AutoHoldingTimer = {
    Message: string
    Timestamp: number
    Type: AutoHoldingType
}

export enum MessageViewType {
    Edit,
    Quote,
    Message
}

export type MessageInputViewData = {
    type: MessageViewType.Edit | MessageViewType.Quote
    message: UpdatedMessage
    messageList?: Message[]
}
type MessageDisplayViewData = {
    type: MessageViewType.Message
    message: QuotedMessage
    messageList?: Message[]
}

export type MessageViewData = MessageInputViewData | MessageDisplayViewData

export type UserIsTypingMessage = {
    isTyping: boolean
    message: string
}

export type SendEventRequest = {
    ProjectId: string
    OmniUserId: string
    ChannelId: string
    Reason?: string
}
